import {
  createBillingSqlExecutorFromManager,
  resolveBillingConnectionIdByWebhookIngressToken,
} from "../billing/import/postgres.ts";
import { billingWebhookIngressObservabilityFromInternals } from "../billing/ingestion/observability/from-internals.ts";
import { recordBillingWebhookIngressQuietly } from "../billing/ingestion/observability/health.ts";
import {
  billingWebhookRegistrationKind,
  persistableWebhookRejectionStage,
  publicBillingRejectionCode,
} from "../billing/ingestion/observability/stages.ts";
import {
  BILLING_MOLLIE_CLASSIC_WEBHOOK_PATH,
  BILLING_MOLLIE_EVENTS_WEBHOOK_PATH,
  billingWebhookIngressBindingToken,
  billingWebhookPathKind,
} from "../billing/ingestion/urls.ts";
import {
  classifyMollieWebhookBody,
  expectedMollieWebhookEnvelope,
  formatMollieWebhookAttemptedBody,
} from "../billing/runtime/local/providers/mollie/parse-webhook.ts";
import { MOLLIE_WEBHOOK_MAX_BODY_BYTES } from "../billing/runtime/local/providers/mollie/verify-webhook.ts";
import {
  getAthenaClientInternals,
  requireAthenaRootClientInternals,
} from "../runtime/client-internals.ts";
import { compileHttpWebhookIngress } from "../runtime/ingress/compiler/http.ts";
import {
  ATHENA_EVENT_INGRESS_FAILED,
  ATHENA_EVENT_INGRESS_PERSISTENCE_REQUIRED,
  ATHENA_EVENT_INGRESS_UNAVAILABLE,
  AthenaEventIngressError,
  eventIngressHttpStatus,
  isAthenaEventIngressError,
} from "../runtime/ingress/errors.ts";
import type { EventIngressRuntime } from "../runtime/ingress/runtime.ts";
import { isAthenaHttpBodyLimitError } from "../runtime/transport/http/incoming.ts";

export const DEFAULT_ATHENA_NEXT_BILLING_WEBHOOK_ENDPOINT =
  "/api/athena/billing/webhook";
export const DEFAULT_ATHENA_NEXT_BILLING_MOLLIE_CLASSIC_WEBHOOK_ENDPOINT =
  BILLING_MOLLIE_CLASSIC_WEBHOOK_PATH;
export const DEFAULT_ATHENA_NEXT_BILLING_MOLLIE_EVENTS_WEBHOOK_ENDPOINT =
  BILLING_MOLLIE_EVENTS_WEBHOOK_PATH;

export interface AthenaBillingIngressHandlers {
  POST: (request: Request) => Promise<Response>;
}

export interface CreateAthenaBillingIngressHandlersOptions {
  client: object;
  connectionId?: string;
}

async function resolveIngressConnectionId(input: {
  client: object;
  explicitConnectionId?: string;
  pathname: string;
}): Promise<string | undefined> {
  if (
    input.explicitConnectionId != null &&
    input.explicitConnectionId.length > 0
  ) {
    return input.explicitConnectionId;
  }
  const token = billingWebhookIngressBindingToken(input.pathname);
  if (token == null) {
    return;
  }
  const internals = getAthenaClientInternals(input.client);
  const manager = internals?.postgresRuntime
    ? await internals.postgresRuntime.getPoolManager()
    : undefined;
  if (!manager) {
    throw new AthenaEventIngressError({
      code: ATHENA_EVENT_INGRESS_PERSISTENCE_REQUIRED,
      domain: "billing",
      message:
        "Billing webhook ingress bindings require PostgreSQL so the connection can be resolved.",
      retryable: true,
    });
  }
  const connectionId = await resolveBillingConnectionIdByWebhookIngressToken(
    createBillingSqlExecutorFromManager(manager),
    token
  );
  if (connectionId == null) {
    return;
  }
  return connectionId;
}

export function createAthenaBillingIngressHandlers(
  options: CreateAthenaBillingIngressHandlersOptions
): AthenaBillingIngressHandlers {
  requireAthenaRootClientInternals(
    options.client,
    "createAthenaBillingIngressHandlers({ client })"
  );

  return {
    async POST(request: Request): Promise<Response> {
      const internals = getAthenaClientInternals(options.client);
      const observability =
        billingWebhookIngressObservabilityFromInternals(internals);
      const pathname = new URL(request.url).pathname;
      const pathKind = billingWebhookPathKind(pathname);
      const operation =
        pathKind === "events"
          ? "webhook.mollie.events"
          : "webhook.mollie.classic";
      const kind = billingWebhookRegistrationKind(operation);
      const runtime = internals?.eventIngressRuntime as
        | EventIngressRuntime
        | undefined;
      if (!runtime) {
        const unavailable = new AthenaEventIngressError({
          code: ATHENA_EVENT_INGRESS_UNAVAILABLE,
          message: "EventIngressRuntime is not attached.",
          retryable: true,
        });
        await recordBillingWebhookIngressQuietly(
          observability,
          async (store) => {
            await store.recordRejected({
              code: unavailable.code,
              kind,
              stage: persistableWebhookRejectionStage(),
              ...(options.connectionId
                ? { connectionId: options.connectionId }
                : {}),
            });
          }
        );
        return new Response(
          JSON.stringify({
            code: unavailable.code,
            message: unavailable.message,
          }),
          { headers: { "content-type": "application/json" }, status: 503 }
        );
      }
      let compiledBody: Uint8Array | undefined;
      let compiledConnectionId: string | undefined;
      try {
        if (pathKind === "compat" && process.env.NODE_ENV === "production") {
          return new Response(null, { status: 404 });
        }
        const token = billingWebhookIngressBindingToken(pathname);
        if (
          (pathKind === "classic" || pathKind === "events") &&
          token == null &&
          (options.connectionId == null || options.connectionId.length === 0)
        ) {
          return new Response(null, { status: 404 });
        }
        if (
          process.env.NODE_ENV === "production" &&
          options.connectionId != null &&
          options.connectionId.length > 0 &&
          token == null
        ) {
          return new Response(null, { status: 404 });
        }
        const connectionId = await resolveIngressConnectionId({
          client: options.client,
          explicitConnectionId: options.connectionId,
          pathname,
        });
        if (
          (pathKind === "classic" || pathKind === "events") &&
          connectionId == null
        ) {
          return new Response(null, { status: 404 });
        }
        const ingress = await compileHttpWebhookIngress({
          domain: "billing",
          operation,
          request,
          ...(connectionId ? { connectionId } : {}),
          maxBodyBytes: MOLLIE_WEBHOOK_MAX_BODY_BYTES,
        });
        compiledBody = ingress.body;
        compiledConnectionId = ingress.connectionId ?? options.connectionId;
        await recordBillingWebhookIngressQuietly(
          observability,
          async (store) => {
            const connectionId = ingress.connectionId ?? options.connectionId;
            await store.recordStage({
              ingressId: ingress.id,
              kind,
              occurredAt: new Date(),
              operation,
              provider: "mollie",
              stage: "received",
              ...(connectionId ? { connectionId } : {}),
              ...(ingress.correlationId
                ? { correlationId: ingress.correlationId }
                : {}),
              ...(ingress.traceId ? { traceId: ingress.traceId } : {}),
            });
            if (connectionId) {
              await store.recordDelivery({
                connectionId,
                event: "received",
                kind,
              });
            }
          }
        );
        const result = await runtime.ingest(ingress);
        const reconciliation = result.reconciliation ?? "skipped";
        return new Response(
          JSON.stringify({
            duplicate: result.duplicate,
            ok: true,
            reconciliation,
          }),
          {
            headers: { "content-type": "application/json" },
            status: result.duplicate ? 200 : 202,
          }
        );
      } catch (error) {
        if (isAthenaHttpBodyLimitError(error)) {
          return new Response(
            JSON.stringify({
              code: error.code,
              message: error.message,
            }),
            {
              headers: { "content-type": "application/json" },
              status: error.status,
            }
          );
        }
        const status = eventIngressHttpStatus(error);
        const code = isAthenaEventIngressError(error)
          ? error.code
          : ATHENA_EVENT_INGRESS_FAILED;
        const contentType = request.headers.get("content-type") ?? undefined;
        const envelope =
          isAthenaEventIngressError(error) && error.diagnostics
            ? {
                bodyBytes: error.diagnostics.bodyBytes,
                bodyKind: error.diagnostics.bodyKind,
                contentType: error.diagnostics.contentType ?? contentType,
              }
            : classifyMollieWebhookBody(
                compiledBody ?? new Uint8Array(),
                contentType
              );
        const expectedEnvelope = expectedMollieWebhookEnvelope(operation);
        const attemptedBody = formatMollieWebhookAttemptedBody(
          compiledBody ?? new Uint8Array(),
          envelope.bodyKind ?? "unknown"
        );
        console.error("[athena.billing] webhook ingress rejected", {
          attemptedBody,
          bodyBytes: envelope.bodyBytes,
          bodyKind: envelope.bodyKind,
          code,
          contentType: envelope.contentType,
          expectedEnvelope,
        });
        await recordBillingWebhookIngressQuietly(
          observability,
          async (store) => {
            await store.recordRejected({
              code: publicBillingRejectionCode(error),
              evidence: {
                attemptedBody,
                expectedEnvelope: { ...expectedEnvelope },
              },
              kind,
              metadata: {
                bodyBytes: envelope.bodyBytes,
                bodyKind: envelope.bodyKind,
                ...(envelope.contentType
                  ? { contentType: envelope.contentType }
                  : {}),
              },
              stage: persistableWebhookRejectionStage(),
              ...(compiledConnectionId
                ? { connectionId: compiledConnectionId }
                : options.connectionId
                  ? { connectionId: options.connectionId }
                  : {}),
            });
          }
        );
        return new Response(
          JSON.stringify({
            code,
            message: isAthenaEventIngressError(error)
              ? error.code
              : "Webhook ingest failed.",
          }),
          {
            headers: { "content-type": "application/json" },
            status,
          }
        );
      }
    },
  };
}
